import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PartnershipSetupService {

  constructor(private http: HttpClient) {}

  /**
   * Replicates JsonObjectFactory.getJSONObj merge behaviour:
   * 1. Start with globalParams
   * 2. Overlay filterParams
   * 3. Overlay the specific action params
   * All values are coerced to strings (HTTP GET params must be strings).
   */
  private buildGetParams(
    globalService: any,
    filterParams: any,
    actionParams: Record<string, any>
  ): Record<string, string> {
    const params: Record<string, string> = {};

    // 1. globalParams — lowest priority
    const globalParams = globalService?.globalParams || {};
    Object.keys(globalParams).forEach(key => {
      const val = globalParams[key];
      if (val !== undefined && val !== null) {
        params[key] = String(val);
      }
    });

    // 2. filterParams — overrides globalParams
    if (filterParams) {
      Object.keys(filterParams).forEach(key => {
        const val = filterParams[key];
        if (val !== undefined && val !== null) {
          params[key] = String(val);
        }
      });
    }

    // 3. Specific action params — highest priority (overrides both)
    Object.keys(actionParams).forEach(key => {
      const val = actionParams[key];
      if (val !== undefined && val !== null) {
        params[key] = String(val);
      }
    });

    return params;
  }

  /**
   * Load partner list for the partnership setup form.
   * Replicates AngularJS: JsonObjectFactory.getJSONObj(url, {"action_code":'3nizjt', ...})
   */
  loadPartnerList(
    baseUrl: string,
    filterParams: any,
    globalService: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;

    const ssoId = globalService?.userSettings?.user?.sso_id
      || globalService?.globalParams?.sso_id
      || '';

    const params = this.buildGetParams(globalService, filterParams, {
      action_code: '3nizjt',
      tax_year: filterParams.tax_year || globalService?.globalParams?.tax_year,
      scenario: filterParams.scenario || globalService?.globalParams?.scenario,
      jcd_key: filterParams.jcd_key || globalService?.globalParams?.jcd_key,
      pship_cc: rowData.COMBINATION_KEY,
      sso_id: ssoId
    });

    return this.http.get<any>(url, { params, withCredentials: true });
  }

  /**
   * Load posting combo LOV list.
   * Replicates AngularJS: JsonObjectFactory.getJSONObj(url, {"action_code":'j6y2x9', ...})
   */
  loadPostingCombos(
    baseUrl: string,
    filterParams: any,
    globalService: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;

    const ssoId = globalService?.userSettings?.user?.sso_id
      || globalService?.globalParams?.sso_id
      || '';

    const params = this.buildGetParams(globalService, filterParams, {
      action_code: 'j6y2x9',
      tax_year: filterParams.tax_year || globalService?.globalParams?.tax_year,
      scenario: filterParams.scenario || globalService?.globalParams?.scenario,
      leid: rowData.PSHIP_LEID,
      cdr_no: rowData.PSHIP_TAX_CODE,
      rp: rowData.PSHIP_REPORTING_PERIOD,
      me_code: rowData.PSHIP_BUSINESS,
      currency: rowData.PSHIP_CURRENCY,
      sso_id: ssoId
    });

    return this.http.get<any>(url, { params, withCredentials: true });
  }

  /**
   * Save partnership setup form.
   * Replicates AngularJS PartnershipServicesFactory.savePartnershipSetupForm:
   *   params = _.merge({}, GlobalService.globalParams, filterParams)
   *   params.clob_data = JSON.stringify(data)
   *   params.clob_settings = JSON.stringify(settings)
   *   params.tax_year / scenario / jcd_key / process_name / sso_id overrides
   */
  savePartnershipSetupForm(
    baseUrl: string,
    clobSettings: any,
    clobData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=n2pswc`;

    const jsonObj = JSON.stringify(clobData);
    const jsonSettings = JSON.stringify(clobSettings);

    // Replicate _.merge({}, GlobalService.globalParams, filterParams)
    const merged: any = {
      ...(globalService?.globalParams || {}),
      ...filterParams
    };

    // Apply clob fields and explicit overrides (matching AngularJS service exactly)
    merged.clob_data = jsonObj;
    merged.clob_settings = jsonSettings;
    merged.tax_year = filterParams.tax_year || merged.tax_year;
    merged.scenario = filterParams.scenario || merged.scenario;
    merged.jcd_key = globalService?.globalParams?.jcd_key || filterParams.jcd_key || merged.jcd_key;
    merged.process_name = 'Partnership Partner Setup';
    merged.sso_id = clobSettings.sso_id
      || globalService?.userSettings?.user?.sso_id
      || globalService?.globalParams?.sso_id
      || '';

    // Encode as application/x-www-form-urlencoded (required by backend)
    const urlEncoded = new URLSearchParams();
    Object.keys(merged).forEach(key => {
      if (merged[key] !== undefined && merged[key] !== null) {
        urlEncoded.set(key, merged[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
